import { useCallback, useEffect, useRef, useState } from 'preact/hooks';

import { Unpacked } from '../utils/types';

import { usePlayer } from './PlayerProvider';
import { useJWPlayerEvent } from './useJWPlayerEvent';
import { useVideoElement } from './useVideoElement';

const events = [
  'buffer',
  'play',
  'pause',
  'idle',
  'adPlay',
  'adPause',
  'complete',
] as const;

const BUFFERING_STATE_DELAY_IN_MILLISECONDS = 1000;

export function usePlayerState() {
  const player = usePlayer();
  const videoElement = useVideoElement();
  const timeoutRef = useRef<number>();
  const [state, setState] = useState(
    player.getJWPlayer()?.getState() ?? 'idle'
  );

  const onStateChange = useCallback(
    (param: jwplayer.EventParams[Unpacked<typeof events>]) => {
      window.clearTimeout(timeoutRef.current);

      let newState: typeof state;

      if (param && 'newstate' in param) {
        newState = param.newstate as typeof state;
      } else {
        newState = player.getJWPlayer()?.getState() ?? 'error';
      }

      /**
       * Player will change to the "buffering" state only if video is buffering for longer than
       * one second, this way we avoid unnecessary flickers of UI.
       */
      if (newState === 'buffering') {
        timeoutRef.current = window.setTimeout(() => {
          setState(newState);
        }, BUFFERING_STATE_DELAY_IN_MILLISECONDS);
      } else {
        setState(newState);
      }
    },
    [player]
  );

  // jwplayer doesn't emit pause event when iOS Safari pauses a video due to autoplay policy
  // see https://developer.apple.com/documentation/webkit/delivering_video_content_for_safari#3030251
  useEffect(() => {
    if (!videoElement) {
      return;
    }

    const handler = () => {
      setState('paused');
    };

    videoElement.addEventListener('pause', handler);

    return () => {
      videoElement.removeEventListener('pause', handler);
    };
  }, [videoElement]);

  useEffect(() => {
    return () => window.clearTimeout(timeoutRef.current);
  }, []);

  useJWPlayerEvent(events, onStateChange);

  return state;
}
